import dayjs from 'dayjs';
import { IFeature } from './types';

/**
 * קטלוג היכולות של דף "מה חדש".
 *
 * `imageFile` / `videoFile` הם שמות קבצים בלבד. מעלים אותם דרך קונסולת AWS
 * לתיקייה `data/_app/whats-new/<version>/` בדלי `nihulkav` (הגרסה מוגדרת
 * ב-`WHATS_NEW_MEDIA_VERSION` ב-`api/app/whats-new-media.api.ts`), והשרת מזרים
 * אותם משם.
 *
 * להוספת יכולת לסבב חדש:
 *   1. מעלים לתיקיית הגרסה `<id>.webp`, ואם יש הדגמה גם `<id>.mp4`.
 *   2. מוסיפים כאן אובייקט עם `section: 'spotlight'` ומעבירים את הסבב הקודם ל-`'catalog'`.
 *   3. מעלים את `ANNOUNCEMENT.version` ב-`announcement.ts`.
 */

/**
 * לינק לקולאז' לדוגמה שהלקוחות יכולים לפתוח. מפרסמים קולאז' אמיתי במסך
 * לקוחות → קולאז' שיווקי, מעתיקים את הלינק ומדביקים כאן. הלינק נראה כך:
 * `https://nihulkav.shop/v2/collage?pass=<company_id>_<token>` (see ORDER_SHOP_HOST).
 * כל עוד הערך ריק — כפתור "לצפייה בדוגמה" לא מוצג.
 */
const COLLAGE_DEMO_URL = '';

export const features: IFeature[] = [
    {
        id: 'ai-assistant',
        section: 'hero',
        title: 'עוזר אישי — AI ניהול קו',
        shortDescription:
            'עוזר אישי בתוכנה או בוואטסאפ — מדברים איתו כמו עם בן אדם. כותבים מה רוצים לדעת, ואילו פעולות רוצים לעשות: להפיק מסמכים, להוציא קבלות, להביא נתונים ודוחות — והוא מבצע באופן בטוח, ושואל אם משהו לא ברור.',
        description:
            'עוזר אישי בתוכנה או בוואטסאפ — מדברים איתו כמו עם בן אדם. כותבים מה רוצים לדעת, ואילו פעולות רוצים לעשות: להפיק מסמכים, להוציא קבלות, להביא נתונים ודוחות — והוא מבצע באופן בטוח, ושואל אם משהו לא ברור.\n\nלפני כל דבר שמשנה נתונים או נשלח ללקוח הוא מחכה לאישור שלכם. אם הוא לא מבין שם של לקוח או מוצר — הוא לא מנחש, הוא שואל.',
        imageFile: 'ai-assistant.webp',
        badge: 'AI',
        tags: ['AI', 'וואטסאפ', 'אפליקציה', 'חיסכון בזמן', 'מסמכים'],
        promoHeader: 'מבצע לתקופת החגים',
        pricing: { kind: 'free-trial', label: 'ללא עלות — 0 לניסיון עד 31.10', wasPrice: '₪200' },
        highlights: [
            'עוזר אישי בתוכנה או בוואטסאפ — מדברים איתו כמו עם בן אדם',
            'כותבים מה רוצים לדעת ומה לעשות: מסמכים, קבלות, נתונים ודוחות',
            'תשובות על העסק: חובות, מחזור, מה נמכר ולמי',
            'שליחת מסמכים והודעות ללקוחות',
            'דוחות PDF וקרטסת בפקודה אחת',
            'סיכום בוקר, תזכורות ומשימות שרצות לבד',
            'זיהוי לקוחות ומוצרים בשמות חופשיים',
            'כל פעולה שמשנה נתונים עוברת אישור שלכם',
            'לא מבין משהו — שואל, לא מנחש'
        ],
        instructions: [
            'פונים אלינו בוואטסאפ ומפעילים את העוזר לחברה שלכם',
            'מדברים איתו באתר, באפליקציה בטלפון, או בוואטסאפ',
            'רוצים גם בוואטסאפ? מחברים את המספר שלכם והעוזר עונה שם'
        ],
        releasedAt: dayjs().year(2026).month(7).date(11)
    },
    {
        id: 'auto-collections',
        section: 'spotlight',
        title: '\u200F💰 הגבייה שרצה גם כשאתה לא רודף אחריה',
        shortDescription:
            'תזכורות בוואטסאפ ובמייל — כל אחד לפי הזמנים והשוטף שלו, שיח עם הלקוח, בדיקת העברות והפקדות בבנק אוטומטית, הוצאת קבלות וסגירת חשבוניות, וניתוח מתמיד לבעיות בחובות ובגבייה — הכל בשלבים, בבקרה שלכם. כולל ניהול תזרים.',
        description:
            'המטרה פשוטה: להוריד מכם בערך 90% מהעבודה הקשה בגבייה. ה־10% שנשארים הם המקרים הקשים — ואלה נשארים אצלכם, כי שם באמת צריך בן אדם.\n\nהכל מתבצע בצורה בטוחה, ובשלבים. מה שברור רץ לבד. מה שלא בטוח — לא מנחש ולא סוגר: נשאר לאישור שלכם.\n\nמתזכר לקוחות על חשבוניות פתוחות בוואטסאפ, במייל או ב־SMS — כל אחד לפי הזמנים והשוטף שלו, ולפי המדיניות שאתם קובעים: מתי, למי, באיזה טון, ומתי להפסיק. מי ששילם לא מקבל תזכורת מיותרת.\n\nאם הלקוח צריך הסבר — המערכת מדברת איתו ושולחת כרטסת או חשבוניות. אתם קובעים את הגבולות; היא לא עושה דברים על דעת עצמה.\n\nמוודאת תשלומים בפועל: בודקת אוטומטית העברות והפקדות בחשבון הבנק, מקשרת אותן ללקוח, וכשהזיהוי ברור מוציאה קבלה וסוגרת את החשבונית. תנועה שלא זוהתה? אתם מלמדים פעם אחת מי המעביר — ומכאן זה אישי: המערכת זוכרת את הלקוח שלכם.\n\nכל הזמן מנתחת את החובות והגבייה: מי חורג, מי נתקע, איפה יש בעיה — כדי שתראו את הקשים בזמן, ולא אחרי שכבר מאוחר.\n\nכולל ניהול תזרים: רואים מה נכנס, מה עוד צפוי, ומה הפער — לא רק חוב בספרים, אלא מתי הכסף באמת מגיע.\n\nאתם שולטים על הכל. אפשר לעצור, לאשר, לשנות מדיניות ולראות כל פעולה. הגבייה רצה בשבילכם — בלי לוותר על השליטה ועל הביטחון.',
        imageFile: 'auto-collections.webp',
        badge: 'גבייה',
        tags: ['גבייה', 'תזרים', 'וואטסאפ', 'מייל', 'SMS', 'אוטומציה', 'AI'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        goalPath: {
            label: 'המטרה',
            steps: ['10%', '20%', '50%', '90%', '100% מהלקוחות'],
            caption: 'למזער לחלוטין את הטיפול בלקוחות — ולתת לרובוט לטפל בכל העבודה השחורה.'
        },
        highlights: [
            '90% מהעבודה הקשה יורדת מכם — 10% הקשים נשארים אצלכם',
            'תזכורות בוואטסאפ, מייל ו־SMS — כל לקוח לפי הזמנים והשוטף שלו',
            'שיח עם הלקוח ושליחת כרטסת או חשבוניות כשצריך',
            'בדיקה אוטומטית של העברות והפקדות בבנק — מוודאים ששילמו באמת',
            'הוצאת קבלה וסגירת חשבונית כשהזיהוי ברור',
            'ניתוח מתמיד של החובות והגבייה — זיהוי בעיות בזמן',
            'ניהול תזרים: מה נכנס, מה צפוי, ומה הפער',
            'פרסונלי: זוכרת כל לקוח, כל מעביר, ואת המדיניות שלכם',
            'שליטה מלאה: מה שלא בטוח נשאר לאישור — לא רץ לבד'
        ],
        instructions: [
            'פונים אלינו להפעלה ולחיבור חשבון הבנק',
            'מגדירים את המדיניות שלכם: למי מתזכרים, באיזה ערוץ, ומתי לעצור',
            'המערכת רצה בשלבים — מה שברור מתבצע, מה שלא בטוח מגיע אליכם לאישור',
            'תנועה חדשה בבנק: מלמדים פעם אחת מי המעביר, והקשר נשמר אישית להבא'
        ]
    },
    {
        id: 'automatic-orders',
        section: 'spotlight',
        title: 'קליטת הזמנות אוטומטית מלקוחות בכל צורה אפשרית',
        shortDescription:
            'תזכורות אוטומטיות ללקוחות לפי הימים שלהם לבצע הזמנה, וקליטת הזמנות אוטומטית בכל צורה אפשרית: אפליקציית הזמנות ייעודית, הודעות וואטסאפ, מסמכי PDF, מיילים, קבצי XLSX, הקלטות מהוואטסאפ ותמונות. לכל לקוח זוכרת את השמות שהוא קורא למוצרים, את שיטת ההזמנה שלו ואת השטויות שלו — הכל בצורה בטוחה ובאישור שלכם.',
        description:
            'תזכורות אוטומטיות ללקוחות לפי הימים שלהם לבצע הזמנה — בוואטסאפ או במייל. הלקוח מקבל תזכורת ביום שלו, מזמין בדרך שנוחה לו, וההזמנה נכנסת למערכת.\n\nוקליטת הזמנות אוטומטית בכל צורה אפשרית: אפליקציית הזמנות ייעודית, הודעות וואטסאפ, מסמכי PDF, מיילים, קבצי XLSX, הקלטות מהוואטסאפ ותמונות.\n\nלכל לקוח המערכת זוכרת את השמות שהוא קורא למוצרים, את שיטת ההזמנה שלו ואת השטויות שלו — איך הוא כותב, מה הוא מתכוון, ומה חוזר אצלו. מכניסה אוטומטית כשהזיהוי ברור.\n\nהכל בצורה בטוחה ובאישור שלכם. אם היא לא בטוחה — היא לא מנחשת: משאירה לכם להחליט.',
        imageFile: 'automatic-orders.webp',
        badge: 'חיסכון בזמן',
        tags: ['AI', 'הזמנות', 'וואטסאפ', 'מייל', 'תזכורות', 'חיסכון בזמן'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום', note: 'לפי נפח הזמנות' },
        goalPath: {
            label: 'המטרה',
            steps: ['10%', '20%', '50%', '90%', '100% מהלקוחות'],
            caption: 'למזער כמעט לחלוטין את הצורך בהקלדה. תמיד יישאר גרעין קשה של כ־10% — גם אותם נפתור.'
        },
        highlights: [
            'תזכורות אוטומטיות ללקוחות להזמין — לפי הימים שלהם, בוואטסאפ או במייל',
            'קליטה מכל צורה: אפליקציית הזמנות ייעודית, וואטסאפ, PDF, מייל, XLSX, הקלטה ותמונה',
            'לכל לקוח זוכרת את השמות שהוא קורא למוצרים, את שיטת ההזמנה שלו ואת השטויות שלו',
            'הכל בצורה בטוחה ובאישור שלכם — לא בטוחה? לא מנחשת, משאירה לכם להחליט',
            'למזער כמעט לחלוטין את ההקלדה — גרעין קשה של 10%? גם אותם נפתור'
        ],
        instructions: [
            'פונים אלינו כדי לחבר אפליקציית הזמנות, וואטסאפ, מייל או כל ערוץ שמגיע אליכם',
            'מגדירים תזכורות אוטומטיות ללקוחות — מתי להזכיר, ובאיזה ערוץ: וואטסאפ או מייל',
            'הזמנות שנקלטות מוצגות לאישור כשהרובוט לא בטוח — ונכנסות אוטומטית כשהזיהוי ברור',
            'אם משהו לא זוהה: מחליטים פעם אחת, והמערכת זוכרת את ההעדפות של הלקוח להבא'
        ],
        releasedAt: dayjs().year(2026).month(7).date(9)
    },
    {
        id: 'iphone-app',
        section: 'catalog',
        title: 'סוף סוף — ניהול קו באייפון',
        shortDescription: 'אפליקציה לאייפון: הזמנות, תעודות וחשבוניות, כרטיס לקוח, מלאי וקרטסת — הכל מהטלפון.',
        description:
            "עד היום האפליקציה הייתה רק לאנדרואיד, ומעכשיו יש ניהול קו גם באייפון. אותה עבודה שאתם עושים במשרד, מהטלפון: פתיחת הזמנות, ליקוט וסידור, הפקת תעודות משלוח וחשבוניות, כרטיס לקוח עם חובות וקרטסת, מוצרים, מלאי וספקים. אפשר גם לשלוח מסמכים ללקוח ישירות מהטלפון, לבנות קולאז' שיווקי ולעבוד עם עוזר ה-AI — עם אותו שם משתמש וסיסמה של ניהול קו.",
        imageFile: 'iphone-app.webp',
        badge: 'אייפון',
        tags: ['אפליקציה', 'אייפון', 'חיסכון בזמן'],
        pricing: { kind: 'included', label: 'כלול במנוי' },
        highlights: [
            'הזמנות: פתיחה, עריכה, ליקוט וסידור',
            'הפקת תעודות משלוח וחשבוניות מהטלפון',
            'כרטיס לקוח, חובות וקרטסת',
            'מוצרים, מלאי וספקים',
            'שליחת מסמכים ללקוח מהטלפון',
            "קולאז' שיווקי ועוזר ה-AI גם בנייד"
        ],
        instructions: [
            'פונים אלינו ומקבלים קישור להתקנה על האייפון',
            'נכנסים עם אותו שם משתמש וסיסמה של ניהול קו',
            'עובדים מהטלפון — הנתונים מתעדכנים מול המשרד'
        ],
        releasedAt: dayjs().year(2026).month(7).date(13)
    },
    {
        id: 'attendance-clock',
        section: 'catalog',
        title: '\u200F⏰ שעון נוכחות',
        shortDescription: 'באפליקציה, דוגם GPS של העובדים, ומציג טבלת נוכחות.',
        description:
            'שעון נוכחות באפליקציה: העובד מנקד כניסה ויציאה מהטלפון, והמערכת דוגמת GPS — רואים איפה דיווח, לא רק מתי.\n\nבמשרד מוצגת טבלת נוכחות: מי נכנס, מי יצא, משך היום, מיקום, היעדרויות וסיכומים לפי עובד. בלי דפים ובלי לרדוף אחרי דיווחים.',
        imageFile: 'attendance-clock.webp',
        badge: 'נוכחות',
        tags: ['נוכחות', 'אפליקציה', 'GPS', 'חיסכון בזמן'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        promoChips: ['באפליקציה', 'דגימת GPS', 'טבלת נוכחות'],
        highlights: [
            'כניסה ויציאה באפליקציה — מהטלפון',
            'דגימת GPS של העובדים בזמן הדיווח',
            'טבלת נוכחות במשרד: שעות, מיקום וסיכומים',
            'רואים מי דיווח ומי לא',
            'בלי דפים ובלי לרדוף אחרי דיווחים'
        ],
        instructions: [
            'פונים אלינו להפעלת שעון הנוכחות לחברה',
            'העובדים מנקרים כניסה ויציאה באפליקציה — ה-GPS נדגם אוטומטית',
            'במשרד: משתמשים → נוכחות, ורואים את הטבלה'
        ],
        ctaPath: '/attendance',
        releasedAt: dayjs().year(2026).month(8).date(7)
    },
    {
        id: 'credit-clearing',
        section: 'catalog',
        title: 'סליקה בניהול קו — אשראי, לינק לתשלום וקבלה אוטומטית',
        shortDescription:
            'מחברים את ניהול קו לסליקה וסולקים לקוחות באשראי — או שולחים לינק לתשלום, וברגע שהלקוח משלם יוצאת קבלה אוטומטית.',
        description:
            'מחברים את ניהול קו לסליקת אשראי וסולקים לקוחות ישירות מהמערכת: מכרטיס לקוח, ממסך החובות או מהפקת קבלה. אפשר לחייב כרטיס שמור או כרטיס חדש, ואפשר גם לשלוח ללקוח לינק לתשלום בוואטסאפ — הוא פותח, משלם, וברגע שהתשלום מאושר מופקת קבלה אוטומטית בחשבונית ישראל. בלי להעתיק סכומים, בלי להפיק מסמך ידנית, ובלי לרדוף אחרי התשלום.',
        imageFile: 'credit-clearing.webp',
        badge: 'סליקה',
        tags: ['סליקה', 'גבייה', 'וואטסאפ'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        highlights: [
            'סליקת אשראי ישירות מניהול קו',
            'חיוב כרטיס שמור או כרטיס חדש',
            'לינק לתשלום שנשלח ללקוח בוואטסאפ',
            'קבלה אוטומטית ברגע שהתשלום מאושר',
            'עובד ממסך החובות, מכרטיס הלקוח ומהפקת קבלה'
        ],
        instructions: [
            'פונים אלינו לחיבור חשבון הסליקה לניהול קו',
            'מסך חובות או כרטיס לקוח — סולקים באשראי או שולחים לינק לתשלום',
            'הלקוח משלם — והקבלה יוצאת אוטומטית'
        ],
        ctaPath: '/customer/all-debt'
    },
    {
        id: 'base-order-robot',
        section: 'catalog',
        title: 'רובוט הזמנות בסיס — מעדכן כמויות לפי מכר וחזרות',
        shortDescription:
            'מציע עדכון להזמנות בסיס לפי מכר וחזרות. לא נוגע בכמויות עד שאתם מאשרים — ואם הוא לא בטוח, הוא שואל.',
        description:
            'במקום לעדכן ידנית את הזמנות הבסיס, הרובוט מסתכל על מה נמכר בכל חנות ועל החזרות — ומציע כיוונון לכמויות הקבועות. הוא לא משנה כלום על דעת עצמו: אתם רואים את ההצעה ומאשרים מה נכנס. אם דפוס ההזמנה לא ברור או שהשינוי גדול מדי — הוא עוצר ושואל. לימוד פעם אחת על חנות נשמר להבא. אפשר להריץ כל יום, כל שבוע או כל חודש.',
        imageFile: 'base-order-robot.webp',
        badge: 'הזמנות',
        tags: ['AI', 'הזמנות', 'חיסכון בזמן'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        comingSoon: true,
        highlights: [
            'מעדכן הזמנות בסיס קבועות לפי מכר וחזרות',
            'ריצה יומית, שבועית או חודשית — לפי בחירתכם',
            'לומד את דפוס ההזמנה של כל חנות',
            'לא נוגע בכמויות על דעת עצמו — אתם מאשרים',
            'לא בטוח? עוצר ושואל',
            'פחות עודף, פחות חוסר, פחות עבודה ידנית'
        ],
        instructions: [
            'פונים אלינו — נעדכן כשהרובוט יהיה זמין',
            'בוחרים האם לעדכן כל יום, כל שבוע או כל חודש',
            'הרובוט מציע עדכונים לפי מכר וחזרות — אתם מאשרים מה שנכנס'
        ]
    },
    {
        id: 'excel-data',
        section: 'catalog',
        title: 'קורא אקסלים ונתונים נוספים — ומכניס אותם לעבודה',
        shortDescription:
            'קורא אקסל ודוחות שאתם מעלים. אם הוא לא מבין עמודה או שם — הוא שואל, ולא משנה נתונים בלי אישור.',
        description:
            'לא הכל נמצא כבר במערכת. שולחים אקסל, דוח ספק, מחירון או מלאי — והעוזר קורא ומציע מה לעשות עם זה בניהול קו. הוא לא מעדכן מחירים או כמויות על דעת עצמו. אם עמודה, שם מוצר או לקוח לא ברורים — הוא שואל, אתם מלמדים פעם אחת, והקישור נשמר להבא.',
        imageFile: 'excel-data.webp',
        badge: 'נתונים',
        tags: ['AI', 'אקסל', 'חיסכון בזמן'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        comingSoon: true,
        show: false,
        highlights: [
            'קורא קבצי אקסל ודוחות שאתם מעלים',
            'מבין עמודות, כמויות, מחירים ושמות מוצרים',
            'משלב נתונים חיצוניים עם מה שכבר בניהול קו',
            'מתאים גם להזמנות בסיס, מחירונים, מלאי ובדיקות',
            'לא מבין עמודה או שם? שואל — לא מנחש',
            'לא משנה נתונים בלי אישור שלכם'
        ],
        instructions: [
            'פונים אלינו — נעדכן כשהיכולת תהיה זמינה',
            'מעלים אקסל או שולחים אותו בוואטסאפ',
            'העוזר קורא, מציע מה לעשות — ואתם מאשרים. אם משהו לא ברור, הוא שואל'
        ]
    },
    {
        id: 'warehouse-picking',
        section: 'spotlight',
        title: '\u200F📦 מחסן בלי דפים',
        shortDescription:
            'מערכת שמתאימה לתהליך הליקוט שלכם באופן אישי — כל השלבים שצריך. כמה מלקטים במקביל, קרטונים, משטחים, ברקודים ובקרה מלאה.',
        description:
            'מחסן בלי דפים ובלי רשימות מודפסות. המערכת נבנית לפי תהליך הליקוט שלכם באופן אישי: כל השלבים שצריך אצלכם — לא תהליך גנרי שאתם מתאימים אליו, אלא השלבים, הסדר והבקרה שאתם עובדים בהם בפועל.\n\nכמה מלקטים עובדים יחד על אותו ליקוט, עם קרטונים, משטחים, ברקודים ובקרה מלאה בזמן אמת. אפשר לחבר משקל חכם, מדפסת מדבקות, לחלק לפי מסלולי נהגים או לפי סדר אחר — או לבנות כל תהליך ליקוט חכם שתרצו.',
        imageFile: 'warehouse-picking.webp',
        badge: 'ליקוט',
        tags: ['מחסן', 'ליקוט', 'חיסכון בזמן'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום', note: 'לפי מספר עמדות ליקוט' },
        promoChips: [
            'ללא ניירת',
            'סיכומים לליקוט',
            'סיכומים לנהגים',
            'סדר ליקוט',
            'מדפסת מדבקות',
            'הכל במקביל — חוסך זמן!'
        ],
        highlights: [
            'מתאימה לתהליך הליקוט שלכם באופן אישי',
            'כל השלבים שצריך אצלכם — לא תהליך גנרי',
            'כמה מלקטים עובדים במקביל על אותו ליקוט',
            'קרטונים, משטחים וברקודים',
            'בקרה מלאה בזמן אמת — בלי דפים',
            'משקל חכם, מדפסת מדבקות, חלוקה לפי מסלולי נהגים או כל סדר שתבחרו'
        ],
        instructions: [
            'פונים אלינו — בונים את עמדות הליקוט לפי התהליך והשלבים שלכם',
            'העובדים נכנסים לאפליקציית הליקוט מהטלפון או מהטאבלט',
            'הליקוט רץ לפי השלבים שהגדרתם ומתעדכן בזמן אמת'
        ],
        releasedAt: dayjs().year(2026).month(5).date(6)
    },
    {
        id: 'marketing-collage',
        section: 'catalog',
        title: "קולאז' שיווקי של תמונות וסרטונים ללקוחות",
        shortDescription: 'שולחים ללקוחות לינק אחד שמראה מה יש היום — בתמונות ובסרטונים.',
        description:
            "מתקשרים עם הלקוחות דרך קולאז' שיווקי: מעלים תמונות וסרטונים של הסחורה שיש היום, המערכת מייצרת לינק אחד ושולחים אותו ללקוחות בוואטסאפ. הלקוח פותח, רואה בדיוק מה יש — וממשיך להזמין.",
        imageFile: 'marketing-collage.webp',
        badge: "קולאז'",
        tags: ['שיווק', 'וואטסאפ', 'לקוחות'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        highlights: [
            'תמונות וסרטונים של מה שיש היום',
            'לינק אחד שנשלח לכל הלקוחות בוואטסאפ',
            'אפשר להציג מחירים ללקוח',
            "מעדכנים את הקולאז' כל יום מחדש"
        ],
        instructions: ["נכנסים ללקוחות → קולאז' שיווקי", 'מעלים תמונות וסרטונים ולוחצים על יצירת לינק', 'שולחים את הלינק ללקוחות בוואטסאפ'],
        ctaPath: '/customer/marketing-collage',
        demoUrl: COLLAGE_DEMO_URL
    },
    {
        id: 'customer-order-link',
        section: 'catalog',
        title: 'אפליקציית הזמנות או לינק ללקוחות — בלי הקלדה, בלי סיסמה',
        shortDescription:
            'הלקוחות מזוהים אוטומטית מהוואטסאפ — בלי שם משתמש ובלי סיסמה. הם רואים קטלוג עם התמונות שאתם מצלמים, מכניסים הזמנה רגילה או עורכים הזמנות בסיס קבועות, והכל נכנס ישר למערכת. אפשר גם לשלוח תזכורות להזמנה.',
        description:
            'במקום לקלוט הזמנות בטלפון ולהקליד במשרד, הלקוחות מכניסים את ההזמנה בעצמם. שולחים לינק בוואטסאפ או נותנים אפליקציה — והלקוח מזוהה אוטומטית ממספר הוואטסאפ, בלי שם משתמש ובלי סיסמה. הוא רואה קטלוג מוצרים עם התמונות שאתם מצלמים, בוחר כמויות, וההזמנה נכנסת ישר למסך ההזמנות בניהול קו. אפשר גם לתת ללקוחות לערוך את הזמנות הבסיס הקבועות שלהם — כדי שהכמויות השגרתיות יתעדכנו מהטלפון, בלי שיחה ובלי הקלדה. אפשר לשלוח תזכורות להזמנה ללקוחות שעוד לא הזמינו. אתם רואים מה הזמינו בזמן אמת, וממשיכים לליקוט.',
        imageFile: 'customer-order-link.webp',
        badge: 'הזמנות',
        tags: ['הזמנות', 'אפליקציה', 'וואטסאפ', 'חיסכון בזמן'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        highlights: [
            'זיהוי אוטומטי מהוואטסאפ — בלי שם משתמש ובלי סיסמה',
            'הלקוח מכניס את ההזמנה בעצמו, בלי הקלדה במשרד',
            'אפשר לתת ללקוחות לערוך גם הזמנות בסיס קבועות',
            'קטלוג מוצרים עם התמונות שאתם מצלמים',
            'ההזמנה נכנסת ישר למערכת',
            'תזכורות להזמנה ללקוחות שעוד לא הזמינו'
        ],
        instructions: [
            'פונים אלינו להפעלת אפליקציית ההזמנות או לינק הלקוחות',
            'שולחים ללקוח לינק בוואטסאפ — הוא מזוהה אוטומטית ונכנס בלי סיסמה',
            'הלקוח מכניס הזמנה או עורך הזמנת בסיס מהקטלוג — והכל נכנס למערכת. אפשר לשלוח תזכורות למי שעוד לא הזמין'
        ],
        ctaPath: '/orders/link-control'
    },
    {
        id: 'driver-link',
        section: 'catalog',
        title: '\u200F🚚 פחות ק״מ. פחות כאב ראש.',
        shortDescription:
            'תכנון הפצה ומסלולים לכמה ימים ולכמה נהגים — כדי למזער זמן ודלק. מסלול אופטימלי, ולינק או אפליקציה לנהג שלכם או לנהג חיצוני.',
        description:
            'ניהול קו מתכננת את ההפצה ואת המסלולים לכמה ימים ולכמה נהגים יחד — לא רק ליום אחד ולנהג אחד — כדי למזער זמן על הכביש וצריכת דלק.\n\nלכל נהג נבנה המסלול הקצר ביותר לפי כתובות, אילוצים, שעות פעילות ופקקים, עם שיוך לקוחות לקווים לפי אזורים. במקביל נותנים לנהג שלכם או לנהג חיצוני אפליקציה מצומצמת עם הרשאות שאתם קובעים, או לינק אישי יומי בוואטסאפ — בלי הדפסות ובלי PDF. הוא רואה את המסלול, הסיכומים ומה להעמיס, מסמן V לכל לקוח — ואתם רואים בלייב מהמשרד. התוצאה: פחות קילומטרים, פחות זמן, פחות דלק, ופחות כאב ראש.',
        imageFile: 'route-optimization.webp',
        badge: 'הפצה',
        tags: ['נהגים', 'מסלולים', 'וואטסאפ', 'אפליקציה', 'חיסכון בזמן'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        highlights: [
            'תכנון הפצה ומסלולים לכמה ימים ולכמה נהגים',
            'ממזער זמן על הכביש וצריכת דלק',
            'מסלול קצר ביותר — מתחשב באילוצים, שעות פעילות ופקקים',
            'שיוך חכם של לקוחות לקווים לפי אזורים',
            'אפליקציה מצומצמת או לינק אישי — לנהג שלכם או לנהג חיצוני',
            'בלי הדפסות ובלי PDF עם הכמויות והסיכומים',
            'הנהג מסמן V — ואתם רואים בלייב מהמשרד'
        ],
        instructions: [
            'פונים אלינו להפעלת אופטימיזציית המסלולים ואפליקציית הנהג או לינק הנהג',
            'מתכננים הפצה לכמה ימים ולכמה נהגים — בהזמנות מפעילים אופטימיזציה, המסלול מסתדר, והלינק נשלח לנהג בוואטסאפ או שהוא נכנס לאפליקציה',
            'עוקבים במשרד בזמן אמת אחרי הלקוחות שסומנו כבוצעו'
        ]
    },
    {
        id: 'automatic-documents',
        section: 'spotlight',
        title: '\u200F🧾 ספקים — ניהול רכש אוטומטית!',
        shortDescription:
            'קליטת מסמכי ספקים אוטומטית עם AI: סריקה, PDF, מיילים, חשבוניות ותעודות — להכניס למלאי ולבדוק מחירי עלות.',
        description:
            'קליטת מסמכי ספקים אוטומטית עם AI — מכל צורה: סריקה, PDF, מיילים, חשבוניות ותעודות. המערכת מזהה מוצרים, כמויות ומחירי קנייה, ומציעה להכניס למלאי ולבדוק מחירי עלות.\n\nהיא לא מכניסה סחורה על דעת עצמה: אתם עוברים ומאשרים. אם מוצר לא מזוהה — היא לא מנחשת, היא שואלת, אתם מלמדים פעם אחת, והקישור נשמר להבא.\n\nאחרי הקליטה בודקים מחירי עלות ומשווים מול מה שנמכר ללקוחות — כדי לראות מרווח, חריגות במחיר קנייה, והאם המחיר ללקוח עדיין נכון. במקום אחד גם כרטיס ספק, חובות והגשה לרואה חשבון.',
        imageFile: 'automatic-documents.webp',
        badge: 'ספקים',
        tags: ['ספקים', 'מסמכים', 'מלאי', 'AI', 'מייל', 'PDF'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום', note: 'לפי כמות מסמכים' },
        goalPath: {
            label: 'המטרה',
            steps: ['10%', '20%', '50%', '90%', '100% מהספקים'],
            caption: 'למזער את הצורך להקליד את המסמכים של הספקים.'
        },
        highlights: [
            'קליטה אוטומטית עם AI: סריקה, PDF, מיילים, חשבוניות ותעודות',
            'הכנסה למלאי אחרי אישור שלכם',
            'בדיקת מחירי עלות והשוואה למחירי לקוחות',
            'מוצר לא מזוהה? שואלת — לא מנחשת',
            'לימוד פעם אחת נשמר להבא',
            'כרטיס ספק, חובות והגשה לרואה חשבון'
        ],
        instructions: [
            'פונים אלינו להפעלת קליטת מסמכי הספקים',
            'שולחים או מעלים חשבוניות ותעודות — סריקה, PDF או מייל',
            'עוברים על מה שנקלט, מאשרים — ורק אז הסחורה נכנסת למלאי. מה שלא זוהה: מלמדים פעם אחת'
        ],
        ctaPath: '/supplier/supplier-list'
    },
    {
        id: 'whatsapp-broadcasts',
        section: 'catalog',
        title: 'שליחת הודעות, מסמכים ותזכורות אוטומטיות — מהוואטסאפ והמייל שלכם',
        shortDescription: 'הלקוחות מקבלים הודעות ומסמכים מהוואטסאפ ומהמייל שלכם, כולל תזכורות שנשלחות אוטומטית.',
        description:
            'שולחים ללקוחות הודעות, חשבוניות, תעודות ותזכורות ישירות מהוואטסאפ ומהאימייל שלכם — הלקוח רואה שההודעה הגיעה מכם ולא ממערכת זרה. את התזכורות אפשר להגדיר שיישלחו אוטומטית, בלי שמישהו יצטרך לזכור לשלוח.',
        imageFile: 'whatsapp-broadcasts.webp',
        badge: 'וואטסאפ',
        tags: ['וואטסאפ', 'מסמכים', 'אוטומציה', 'שיווק'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        highlights: [
            'נשלח מהוואטסאפ ומהמייל שלכם',
            'תזכורות אוטומטיות ללקוחות',
            'צירוף חשבוניות, תעודות ותמונות',
            'שליחה מרוכזת לקבוצת לקוחות'
        ],
        instructions: [
            'מחברים את הוואטסאפ מהצ׳יפ בכותרת התוכנה',
            'בוחרים לקוחות ומסמכים ושולחים',
            'מגדירים אילו תזכורות יישלחו אוטומטית ועוקבים במסך ההודעות'
        ],
        ctaPath: '/customer/sent-messages'
    },
    {
        id: 'external-ai-integration',
        section: 'catalog',
        title: 'חיבור ניהול קו לתוכנה חיצונית או ל-AI — GPT, Claude וכל מערכת אחרת',
        shortDescription: 'שער API מאובטח שדרכו מערכת חיצונית או עוזר AI שלכם שולטים בניהול קו — ואת המערכת שלכם נכניס לתוך ניהול קו.',
        description:
            'יש לכם תוכנה, אפליקציה או אוטומציה משלכם? אנחנו פותחים לכם שער API מאובטח לניהול קו, וממנו אפשר גם לשלוף נתונים וגם לבצע פעולות: לקוחות ומוצרים, חובות והתיישנות, מחזור ומכירות, הפקת מסמכים ושליחת הודעות. אותו שער עובד גם עם כלי AI כמו GPT או Claude, כך שאפשר לנהל את המערכת בשפה חופשית מהכלים שאתם כבר עובדים בהם. ההרשאות נקבעות לכל מפתח בנפרד — אפשר לתת קריאה בלבד, או גם יצירה ועדכון. ואם בניתם מערכת חיצונית משלכם, אנחנו מטמיעים אותה כמסך בתוך ניהול קו כדי שהצוות יעבוד במקום אחד.',
        imageFile: 'external-ai-integration.webp',
        badge: 'אינטגרציה',
        tags: ['AI', 'אינטגרציה', 'API'],
        pricing: { kind: 'contact', label: 'בהתאמה אישית' },
        highlights: [
            'שער API מאובטח עם מפתח לכל חיבור',
            'הרשאות מדויקות: קריאה בלבד או גם יצירה ועדכון',
            'חיבור לכלי AI כמו GPT ו-Claude',
            'שליפת נתונים: לקוחות, חובות, מכירות ומסמכים',
            'ביצוע פעולות: הפקת מסמכים ושליחת הודעות',
            'המערכת שלכם מוטמעת כמסך בתוך ניהול קו'
        ],
        instructions: [
            'מספרים לנו לאיזו מערכת או לאיזה כלי AI רוצים להתחבר',
            'אנחנו מנפיקים מפתח גישה עם ההרשאות שתבחרו',
            'המערכת החיצונית שולפת נתונים ומבצעת פעולות — ואם תרצו, נטמיע אותה כמסך בתוך ניהול קו'
        ]
    },
    {
        id: 'stornext-edi',
        section: 'catalog',
        title: 'חיבור ל-StoreNext EDI וקובץ 100 לשידור לרשתות',
        shortDescription:
            'משדרים חשבוניות ותעודות לרשתות דרך StoreNext EDI, ומפיקים קובץ 100 מוכן לשידור.',
        description:
            'עובדים מול רשתות? מחברים את ניהול קו ל-StoreNext EDI ומשדרים מהמערכת חשבוניות ותעודות משלוח — בלי להעתיק קבצים ובלי להקליד שוב בממשק של הרשת. במסמך רואים אם נשלח, ואפשר לשלוח מחדש.\n\nבמקביל מפיקים קובץ 100 (פורמט EDI לרשתות) לפי מספר חשבונית — קובץ מוכן לשידור לרשת. בכרטיס הלקוח שומרים את מספרי ה-EDI של הרשת, הסניף והספק, כדי שהשידור ייצא מדויק.',
        imageFile: 'stornext-edi.webp',
        badge: 'רשתות',
        tags: ['רשתות', 'מסמכים', 'אינטגרציה'],
        pricing: { kind: 'paid', label: 'בתוספת תשלום' },
        highlights: [
            'חיבור StoreNext EDI — שידור חשבוניות ותעודות לרשתות',
            'סטטוס נשלח / לא נשלח על המסמך, כולל שליחה מחדש',
            'הפקת קובץ 100 לשידור לרשת לפי מספר חשבונית',
            'מספרי EDI של רשת, סניף וספק בכרטיס הלקוח',
            'בלי הקלדה כפולה ובלי להעביר קבצים ידנית'
        ],
        instructions: [
            'פונים אלינו לחיבור StoreNext EDI ולרשתות שאיתן עובדים',
            'בכרטיס הלקוח ממלאים מספרי EDI (רשת, סניף, ספק)',
            'מהמסמך שולחים לסטורנקסט, ומדוחות מפיקים קובץ 100 לשידור'
        ],
        ctaPath: '/reports/all-reports',
        releasedAt: dayjs().year(2026).month(7).date(25)
    },
    {
        id: 'inventory-management',
        section: 'catalog',
        title: 'ניהול מלאי בזמן אמת — יתרות, ספירות ופערים',
        shortDescription:
            'רואים לכל מוצר כמה יש עכשיו, כמה נכנס, כמה יצא לחלוקה, ומה הפער מול הספירה — כולל שתי יחידות מידה וערך עלות.',
        description:
            'מסך מלאי אחד שמראה את התמונה המלאה: מלאי כניסה, כמות חלוקה, יתרה יומית וסה״כ מלאי לכל מוצר. עושים ספירה במחסן — מהטלפון, מהטאבלט או מהמשרד — והמערכת מציגה מיד את הפער מול מה שנרשם. אפשר לעבוד בשתי יחידות מידה, לראות תנועות לכל מוצר, לייבא ספירה מאקסל, ולקלוט מלאי גם מתעודות ספקים. הסוכנים רואים מלאי עדכני לפי משתמש, כולל מלאי שלילי, ומשווים כמויות מול הספירות.',
        imageFile: 'inventory-management.webp',
        badge: 'מלאי',
        tags: ['מלאי', 'מחסן', 'חיסכון בזמן'],
        pricing: { kind: 'included', label: 'כלול במנוי' },
        highlights: [
            'יתרת מלאי לכל מוצר בזמן אמת',
            'ספירה במחסן והצגת פערים מול הרשום',
            'שתי יחידות מידה לכל מוצר',
            'תנועות מלאי, עלות וערך המלאי',
            'קליטת מלאי מתעודת ספק, מאקסל או עם AI',
            'מלאי לפי סוכן, כולל מלאי שלילי'
        ],
        instructions: [
            'נכנסים למוצרים → מלאי',
            'רואים יתרות, מוסיפים תעודת מלאי או מייבאים ספירה',
            'מהטלפון אפשר לספור במחסן ולראות פערים מול הרשום'
        ],
        ctaPath: '/products/inventory'
    }
];

export const visibleFeatures = features.filter((feature) => feature.show !== false);

export const heroFeatures = visibleFeatures.filter((feature) => feature.section === 'hero');

/** סדר «חדש עכשיו» ב-RTL: ימין-למעלה, שמאל-למעלה, ימין-למטה, שמאל-למטה. */
const SPOTLIGHT_ORDER = ['automatic-orders', 'auto-collections', 'warehouse-picking', 'automatic-documents'];

export const spotlightFeatures = visibleFeatures
    .filter((feature) => feature.section === 'spotlight')
    .sort((a, b) => {
        const ai = SPOTLIGHT_ORDER.indexOf(a.id);
        const bi = SPOTLIGHT_ORDER.indexOf(b.id);
        return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
    });

export const catalogFeatures = visibleFeatures.filter((feature) => feature.section === 'catalog');
